/**
 * Migración única de datos localStorage → backend API.
 *
 * Detecta si el usuario tiene datos guardados en localStorage (de la versión
 * anterior sin backend), los envía al backend, y luego marca la migración
 * como completada para no repetirla.
 */
import { api } from "../services/api";

const MIGRATION_KEY = "arbo_migrated_to_backend";

// Posibles keys que el frontend pudo haber guardado
const LS_KEYS = {
  data: "arbo_data",        // { 0: { ingresos, fijos, extras, ahorroMetas, ahorroReal }, ... }
  metas: "arbo_metas",      // [{ id, nombre, objetivo, icono }]
  presupuestos: "arbo_presupuestos",  // [{ id, cat, limite }]
  recurrentes: "arbo_recurrentes",    // [{ id, concepto, monto, cat, tipo }]
  msiCompras: "arbo_msi",            // [{ ... }]
};

function readJSON(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function needsMigration() {
  if (localStorage.getItem(MIGRATION_KEY)) return false;
  // Check if any of the old keys exist
  return Object.values(LS_KEYS).some(key => localStorage.getItem(key) !== null);
}

export async function migrateLocalStorageToBackend(year) {
  if (!needsMigration()) return false;

  console.log("[Arbo] Iniciando migración de localStorage → backend...");

  // Marcar ANTES de iniciar para evitar re-siembra si la migración falla parcialmente.
  // Si hay error de red el usuario pierde la migración automática, pero no reaparecen
  // datos borrados. Preferible a re-crear ítems que el usuario ya eliminó.
  localStorage.setItem(MIGRATION_KEY, new Date().toISOString());

  try {
    // 1. Migrar datos de meses (ingresos, fijos, extras, ahorros)
    const monthsData = readJSON(LS_KEYS.data);
    if (monthsData) {
      for (let i = 0; i < 12; i++) {
        const m = monthsData[i] || monthsData[String(i)];
        if (!m) continue;

        // Get or create the mes in backend
        const mesObj = await api.meses.get(year, i + 1);
        const mesId = Array.isArray(mesObj) ? mesObj[0]?.id : mesObj?.id;
        if (!mesId) continue;

        // Migrate ingresos
        for (const ing of (m.ingresos || [])) {
          if (ing.monto > 0) {
            await api.ingresos.create({
              mes_id: mesId,
              concepto: ing.concepto || "Ingreso",
              monto: ing.monto,
              orden: ing.orden || 0,
            });
          }
        }

        // Migrate gastos fijos
        for (const fijo of (m.fijos || [])) {
          if (fijo.monto > 0 && !fijo.esRecurrente) {
            await api.gastos.fijos.create({
              mes_id: mesId,
              concepto: fijo.concepto || "Gasto fijo",
              monto_presupuesto: fijo.monto || 0,
              fecha_limite_dia: fijo.fechaLimite ? Number(fijo.fechaLimite) : null,
              es_recurrente: false,
            });
          }
        }

        // Migrate gastos extras
        for (const extra of (m.extras || [])) {
          if (extra.monto > 0 && !extra.esRecurrente) {
            await api.gastos.extras.create({
              mes_id: mesId,
              concepto: extra.concepto || "Gasto extra",
              monto: extra.monto || 0,
              categoria: extra.cat || "Otros",
              es_recurrente: false,
            });
          }
        }

        // Migrate ahorros (if metas exist already we'll link by index)
        // This will be linked after metas are migrated
      }
    }

    // 2. Migrar metas
    const metasData = readJSON(LS_KEYS.metas);
    const metaIdMap = {}; // old local id → new backend id
    if (metasData && metasData.length > 0) {
      for (const meta of metasData) {
        if (meta.nombre) {
          const result = await api.metas.create({
            nombre: meta.nombre,
            icono: meta.icono || "⭐",
            objetivo: meta.objetivo || 0,
          });
          if (Array.isArray(result) && result[0]) {
            metaIdMap[meta.id] = result[0].id;
          }
        }
      }
    }

    // 3. Migrar ahorros_metas (now that we have the meta ID mappings)
    if (monthsData && Object.keys(metaIdMap).length > 0) {
      for (let i = 0; i < 12; i++) {
        const m = monthsData[i] || monthsData[String(i)];
        if (!m || !m.ahorroMetas) continue;

        const mesObj = await api.meses.get(year, i + 1);
        const mesId = Array.isArray(mesObj) ? mesObj[0]?.id : mesObj?.id;
        if (!mesId) continue;

        for (const ahorro of m.ahorroMetas) {
          const newMetaId = metaIdMap[ahorro.metaId];
          if (newMetaId && ahorro.monto > 0) {
            await api.metas.ahorros.upsert({
              meta_id: newMetaId,
              mes_id: mesId,
              monto: ahorro.monto,
            });
          }
        }
      }
    }

    // 4. Migrar presupuestos
    const presupuestosData = readJSON(LS_KEYS.presupuestos);
    if (presupuestosData && presupuestosData.length > 0) {
      for (const p of presupuestosData) {
        if (p.cat && p.limite > 0) {
          await api.presupuestos.create({
            categoria: p.cat,
            limite: p.limite,
          });
        }
      }
    }

    // 5. Migrar recurrentes
    const recurrentesData = readJSON(LS_KEYS.recurrentes);
    if (recurrentesData && recurrentesData.length > 0) {
      for (const r of recurrentesData) {
        if (r.concepto) {
          await api.recurrentes.create({
            concepto: r.concepto,
            monto: r.monto || 0,
            categoria: r.cat || "Otros",
            tipo: r.tipo || "extra",
            activo: true,
          });
        }
      }
    }

    console.log("[Arbo] Migración completada exitosamente.");
    return true;
  } catch (err) {
    console.error("[Arbo] Error durante migración:", err);
    // No marcamos como migrado para reintentar la próxima vez
    return false;
  }
}
